// Thin fetch wrapper: bearer injection, 401 handling, typed error mapping.
// Ports auth_interceptor.dart + vote_remote_datasource.dart:45 error map.
// All endpoints live here so nothing else hardcodes paths.

import {
  ApiError,
  type ApiErrorCode,
  type Election,
  type Voter,
  electionFromJson,
  voterFromJson,
} from "@/types";
import { clearToken, getToken, saveToken } from "@/lib/token";

const BASE = process.env.NEXT_PUBLIC_API_BASE_URL!;

// fetch() has no timeout of its own: a request that never answers (captive
// portal, dead tower mid-handshake) leaves the caller pending forever, which on
// /confirmation reads as "Submitting..." with no error and no way to retry.
// Same budgets api_constants.dart used — 30s normally, 120s for the auth
// exchange, which follows an SMS round-trip and is legitimately slow.
const TIMEOUT_MS = 30_000;
// The auth exchange follows an SMS round-trip, and the vote is the one
// irreversible write in the app: timing it out at 30s on an EDGE connection
// leaves the voter unsure whether the ballot landed. Flutter gave both 120s.
const LONG_TIMEOUT_MS = 120_000;

export const Endpoints = {
  login: "/api/voters/login",
  impersonate: "/api/voters/inpersonate", // backend typo — real
  me: "/api/voters/me",
  checkPhone: "/api/voters/check-phone",
  ping: "/api/ping",
  elections: "/api/elections",
  electionsActive: "/api/elections/active",
  votes: "/api/votes",
  broadcastingAuth: "/api/broadcasting/auth",
} as const;

// Called when a non-auth endpoint returns 401 (session expired). Wired by the
// store so the flow gate can react. Set via setUnauthorizedHandler.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

const AUTH_PATHS = ["/voters/login", "/voters/inpersonate", "/voters/me"];
const isAuthPath = (p: string) => AUTH_PATHS.some((a) => p.includes(a));

interface RequestOpts {
  method?: "GET" | "POST";
  body?: unknown;
  // Skip the Authorization header (login / impersonate).
  noAuth?: boolean;
  // Override the default request timeout.
  timeoutMs?: number;
}

async function request<T = unknown>(
  path: string,
  opts: RequestOpts = {},
): Promise<{ status: number; data: T }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (!opts.noAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const method = opts.method ?? "GET";
  // A GET is idempotent, so a dropped request can simply be sent again. 200+
  // phones share one cell at the polling station and single requests do fail
  // there; without this the voter saw "No internet connection" on a blip that
  // a second attempt would have survived.
  //
  // POST is deliberately NOT retried: castVote is a POST, and resending one the
  // server may already have accepted risks a double vote. A failed POST stays a
  // failed POST and the voter decides.
  const attempts = method === "GET" ? 3 : 1;

  let res: Response | null = null;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: AbortSignal.timeout(opts.timeoutMs ?? TIMEOUT_MS),
      });
      break;
    } catch (e) {
      lastError = e;
      if (attempt < attempts - 1) await new Promise((r) => setTimeout(r, 800));
    }
  }
  if (!res) {
    // No response = network error (offline / DNS / CORS). Keep session.
    // A timeout is reported separately so the voter is told to retry rather
    // than that they are offline — localizeError maps it to connectionTimedOut.
    const timedOut =
      lastError instanceof DOMException && lastError.name === "TimeoutError";
    throw new ApiError(
      "network",
      timedOut ? "Connection timed out" : "No internet connection",
    );
  }

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (res.status === 401 && !isAuthPath(path)) {
    onUnauthorized?.();
  }

  if (!res.ok) {
    throw mapError(res.status, data);
  }

  return { status: res.status, data: data as T };
}

function msgOf(data: unknown): string | null {
  if (data && typeof data === "object" && "message" in data) {
    const m = (data as { message?: unknown }).message;
    return typeof m === "string" ? m : null;
  }
  return null;
}

function mapError(status: number, data: unknown): ApiError {
  const message = msgOf(data);
  const low = message?.toLowerCase() ?? "";
  let code: ApiErrorCode = "server";

  switch (status) {
    case 401:
      code = "auth";
      return new ApiError(code, message ?? "Authentication required", status);
    case 403:
      if (low.includes("already voted")) code = "already_voted";
      else if (low.includes("not active") || low.includes("not currently active"))
        code = "election_not_active";
      else code = "auth";
      return new ApiError(code, message ?? "Access denied", status);
    case 404:
      code = "not_registered";
      return new ApiError(code, message ?? "Not found", status);
    case 409:
      return new ApiError("already_voted", message ?? "Vote already recorded", status);
    case 422:
      if (low.includes("exactly") && low.includes("candidate"))
        code = "invalid_candidate_count";
      else if (low.includes("duplicate") || low.includes("same candidate"))
        code = "duplicate_candidates";
      else code = "validation";
      return new ApiError(code, message ?? "Validation error", status);
    default:
      if (status >= 500)
        return new ApiError("server", "Server error. Please try again later.", status);
      return new ApiError("server", message ?? "Request failed", status);
  }
}

// ---- Endpoint helpers ----

// Exchange Firebase ID token for a Sanctum bearer; stores the token.
export async function login(firebaseIdToken: string): Promise<Voter> {
  const { data } = await request<Record<string, unknown>>(Endpoints.login, {
    method: "POST",
    body: { token: firebaseIdToken },
    noAuth: true,
    timeoutMs: LONG_TIMEOUT_MS,
  });
  saveToken(data.token as string);
  return voterFromJson(data.voter as Record<string, unknown>);
}

export async function impersonate(phone: string, magicToken: string): Promise<Voter> {
  clearToken();
  const { data } = await request<Record<string, unknown>>(Endpoints.impersonate, {
    method: "POST",
    body: { phone, magic_token: magicToken },
    noAuth: true,
  });
  saveToken(data.token as string);
  return voterFromJson(data.voter as Record<string, unknown>);
}

export async function getMe(): Promise<Voter> {
  const { data } = await request<Record<string, unknown>>(Endpoints.me);
  return voterFromJson(data);
}

// Gate before sending an OTP (Flutter's hasActiveElection check) — no point
// spending a real SMS if nothing is open to vote in right now.
export async function hasActiveElection(): Promise<boolean> {
  const { data } = await request<{ has_active_election?: boolean }>(
    Endpoints.electionsActive,
    { noAuth: true },
  );
  return data.has_active_election ?? false;
}

export async function checkPhone(phone: string): Promise<boolean> {
  const { data } = await request<{ exists?: boolean }>(Endpoints.checkPhone, {
    method: "POST",
    body: { phone },
    noAuth: true,
  });
  return data.exists ?? false;
}

// True if authenticated; throws ApiError("auth") on 401.
export async function ping(): Promise<boolean> {
  await request(Endpoints.ping);
  return true;
}

// Elections the voter is enabled in (empty ⇒ needs prevalidation).
// /api/elections returns SUMMARY items, so — matching the Flutter
// getAllOngoingElections — we fetch full details per election (candidates,
// status, has_voted) via getElectionById, skipping any that 403 (the voter is
// not prevalidated for that one). 401 and other errors propagate.
export async function getElections(): Promise<Election[]> {
  const { data } = await request<unknown>(Endpoints.elections);
  let list: Record<string, unknown>[];
  if (Array.isArray(data)) {
    list = data as Record<string, unknown>[];
  } else if (data && typeof data === "object") {
    const inner = (data as { data?: unknown }).data;
    if (Array.isArray(inner)) list = inner as Record<string, unknown>[];
    else if (inner && typeof inner === "object")
      list = [inner as Record<string, unknown>];
    else return [];
  } else {
    return [];
  }

  const out: Election[] = [];
  for (const item of list) {
    const id = String(item.id);
    try {
      out.push(await getElectionById(id));
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) continue; // not prevalidated
      throw e;
    }
  }
  return out;
}

export async function getElectionById(id: string): Promise<Election> {
  const { data } = await request<Record<string, unknown>>(
    `${Endpoints.elections}/${id}`,
  );
  return electionFromJson(data);
}

// POST /api/votes -> 201. election_id + candidate ids.
export async function submitVote(
  electionId: string,
  candidateIds: string[],
): Promise<void> {
  await request(Endpoints.votes, {
    method: "POST",
    body: { election_id: electionId, candidates: candidateIds },
    timeoutMs: LONG_TIMEOUT_MS,
  });
}
